import React, { FunctionComponent, useEffect, useState } from 'react'
import classNames from 'classnames'
import i18next from 'i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from 'redux'
import {
  Button,
  CircularProgress,
  Hidden,
  LinearProgress,
  Link,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core'
import { Redirect } from 'react-router-dom'
import GoLogo from '@assets/go-logo-graphics/go-main-logo.svg'
import LoginGraphics from '@assets/login-page-graphics/login-page-graphics.svg'
import assetVariant from '../../shared/util/asset-variant'
import { GoGovReduxState } from '../app/reducers/types'
import { GetReduxState } from '../app/actions/types'
import loginActions from './actions'
import rootActions from '../app/components/pages/RootPage/actions'
import { htmlSanitizer } from '../app/util/format'
import { USER_PAGE, VariantType, loginFormVariants } from '../app/util/types'
import { get } from '../app/util/requests'
import LoginForm from './components/LoginForm'
import Section from '../app/components/Section'
import BaseLayout from '../app/components/BaseLayout'
import { GAEvent, GAPageView } from '../app/util/ga'
import TextButton from './widgets/TextButton'
import { initiatedLoginHref, initiatedLoginIssuer } from './sso'

type LoginPageProps = {
  location?: {
    search?: string
    state?: {
      previous: string
    }
  }
}

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexGrow: 1,
      '-ms-flex': '1 1 auto',
    },
    loginContainer: {
      display: 'flex',
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        width: '50%',
      },
    },
    verticalAlign: {
      display: 'flex',
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        alignItems: 'center',
      },
    },
    loginWrapper: {
      display: 'block',
      [theme.breakpoints.up('lg')]: {
        // Gives the contents slightly more than enough height,
        // so that validation messages do not shift the centering.
        height: '400px',
        maxHeight: '80vh',
      },
    },
    headerGroup: {
      marginBottom: theme.spacing(4),
    },
    logo: {
      maxWidth: '130px',
      width: '40%',
    },
    loginHeader: {
      marginTop: theme.spacing(1),
    },
    loginReferral: {
      fontSize: '0.85rem',
      color: '#767676',
      marginBottom: theme.spacing(4),
    },
    textInputGroup: {
      marginBottom: theme.spacing(4),
    },
    graphicColorFill: {
      backgroundColor: theme.palette.primary.dark,
      width: '50vw',
      height: '100%',
      // allocates space for the government masthead when on gov variant
      maxHeight: assetVariant === 'gov' ? 'calc(100vh - 28px)' : '100vh',
      textAlign: 'center',
      overflow: 'hidden',
    },
    loginGraphic: {
      userDrag: 'none',
      height: '100%',
    },
    '@media screen\\0': {
      // Styles for Internet Explorer compatibility
      logoLink: {
        marginBottom: '0',
      },
    },
    secondaryButton: {
      fontWeight: 400,
      marginLeft: theme.spacing(2),
    },
    resendOTPBtn: {
      marginRight: 'auto',
      '&:disabled': {
        opacity: 0.5,
      },
    },
    ssoButton: {
      marginTop: theme.spacing(2),
    },
    handingOff: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: 1,
    },
    handingOffText: {
      marginTop: theme.spacing(2),
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1),
      color: '#767676',
      '&::before, &::after': {
        content: '""',
        flex: 1,
        borderBottom: '1px solid #d8d8d8',
      },
      '&::before': {
        marginRight: theme.spacing(1),
      },
      '&::after': {
        marginLeft: theme.spacing(1),
      },
    },
  }),
)

const LoginPage: FunctionComponent<LoginPageProps> = ({
  location = undefined,
}: LoginPageProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const getEmailValidator = dispatch(
    loginActions.getEmailValidationGlobExpression(),
  )
  // Don't clobber a toast already showing, e.g. the one.gov.sg logout notice.
  const setLoginInfoMessage = (message: string) =>
    dispatch((innerDispatch: Dispatch, getState: GetReduxState) => {
      if (!getState().root.snackbarMessage.message) {
        innerDispatch(rootActions.setInfoMessage(message))
      }
    })
  const emailValidator = useSelector(
    (state: GoGovReduxState) => state.login.emailValidator,
  )
  const isLoggedIn = useSelector(
    (state: GoGovReduxState) => state.login.isLoggedIn,
  )
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [ssoEnabled, setSsoEnabled] = useState(false)
  const iss = initiatedLoginIssuer(
    location?.search ?? '',
    window.location.search,
  )
  // Hide the form while a one.gov.sg-initiated login is being handed off.
  const [handingOff, setHandingOff] = useState(iss !== null)
  const variant: VariantType = useSelector(
    (state: GoGovReduxState) => state.login.formVariant,
  )
  // Google Analytics
  useEffect(() => {
    // Filter out redirects from private routes
    if (!location?.state?.previous) {
      GAPageView('EMAIL LOGIN PAGE')
      GAEvent('login page', 'email')
    }
  }, [location?.state?.previous])

  // Display a login message from the server
  useEffect(() => {
    let cancelled = false
    get('/api/login/message').then((response) => {
      if (response.ok) {
        response.text().then((text) => {
          if (text && !cancelled) setLoginInfoMessage(text)
        })
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Fetch backend validation expression
  useEffect(() => {
    dispatch(loginActions.getEmailValidationGlobExpression())
    return
  }, [getEmailValidator])

  // Menlo keeps its remote-tab pairing in window.name. Left in place, the next
  // one.gov.sg navigation from this tab re-attaches to the stale remote tab
  // (parked on this page) instead of loading authorize, and bounces back here.
  useEffect(() => {
    window.name = ''
  }, [])

  // Check whether one.gov.sg login should be shown. If one.gov.sg launched
  // us (initiate_login_uri), start the login straight away.
  useEffect(() => {
    let cancelled = false
    get('/api/sso/enabled')
      .then((response) => (response.ok ? response.json() : { enabled: false }))
      .then((data) => {
        if (cancelled) return
        if (data.enabled && iss !== null) {
          window.location.replace(initiatedLoginHref(iss))
          return
        }
        setSsoEnabled(!!data.enabled)
        setHandingOff(false)
      })
      // optional feature: stay hidden if discovery fails
      .catch(() => {
        if (!cancelled) setHandingOff(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (handingOff) {
    return (
      <BaseLayout withHeader={false} withFooter={false} withLowFooter={false}>
        <div className={classes.handingOff}>
          <CircularProgress />
          <Typography className={classes.handingOffText} variant="body1">
            Logging in with one.gov.sg
          </Typography>
        </div>
      </BaseLayout>
    )
  }

  if (!isLoggedIn) {
    const variantMap = loginFormVariants.map[variant]
    const isEmailView = loginFormVariants.isEmailView(variant)
    const emailError = () => !!email && !emailValidator(email)

    const formAttr = isEmailView
      ? {
          id: 'email',
          onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            dispatch(loginActions.getOTPEmail(email))
            GAPageView('OTP LOGIN PAGE')
            GAEvent('login page', 'otp', 'successful')
          },
          placeholder: `e.g. ${i18next.t('login.placeholders.email')}`,
          buttonMessage: 'Sign in',
          textError: emailError,
          textErrorMessage: () =>
            emailError()
              ? `This doesn't look like a valid ${i18next.t(
                  'general.emailDomain',
                )} email.`
              : '',
          onChange: (email: string) => setEmail(email.toLowerCase()),
          variant,
          autoComplete: 'on',
          value: email,
        }
      : {
          id: 'otp',
          onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            dispatch(loginActions.verifyOTP(otp))
          },
          titleMessage: 'One time password',
          placeholder: 'e.g. A1B2C3',
          buttonMessage: 'Submit',
          textError: () => false,
          textErrorMessage: () => '',
          onChange: (otp: string) => setOtp(otp),
          variant,
          autoComplete: 'off',
          value: otp,
        }

    return (
      <BaseLayout withHeader={false} withFooter={false} withLowFooter={false}>
        <div className={classes.container}>
          <div>
            <Hidden mdDown>
              <div className={classes.graphicColorFill}>
                <img
                  className={classes.loginGraphic}
                  src={LoginGraphics}
                  alt="Login page graphic"
                />
              </div>
            </Hidden>
          </div>
          <div className={classes.loginContainer}>
            <div className={classes.verticalAlign}>
              <Section>
                <section className={classes.loginWrapper}>
                  <span className={classes.headerGroup}>
                    <Link href="/#/">
                      <img
                        className={classes.logo}
                        src={GoLogo}
                        alt="GoGovSG logo"
                      />
                    </Link>
                    <Typography
                      className={classes.loginHeader}
                      variant="body1"
                      dangerouslySetInnerHTML={{
                        __html: htmlSanitizer(
                          i18next.t('login.whitelistPhrase'),
                        ),
                      }}
                    >
                      {/* <text ></text> NOTE: dangerouslySetInnerHTML is used as copy includes <a href></a> tag */}
                    </Typography>
                    <Typography
                      className={classes.loginReferral}
                      variant="body1"
                    >
                      {i18next.t('login.referrals.1.officerPhrase')} can use
                      their {i18next.t('login.referrals.1.emailDomain')} emails
                      at{' '}
                      <Link
                        href={`https://${i18next.t('login.referrals.1.link')}`}
                      >
                        {i18next.t('login.referrals.1.link')}
                      </Link>
                      , and {i18next.t('login.referrals.2.officerPhrase')} can
                      use their {i18next.t('login.referrals.2.emailDomain')}{' '}
                      emails at{' '}
                      <Link
                        href={`https://${i18next.t('login.referrals.2.link')}`}
                      >
                        {i18next.t('login.referrals.2.link')}
                      </Link>{' '}
                      to shorten links.
                    </Typography>
                  </span>
                  <span className={classes.textInputGroup}>
                    <Typography variant="body1">
                      {isEmailView ? 'Email' : 'One-time password'}
                    </Typography>
                    {/* eslint-disable-next-line */}
                    <LoginForm {...formAttr}>
                      {isEmailView ? (
                        <TextButton
                          className={classes.secondaryButton}
                          href={i18next.t('general.links.faq')}
                        >
                          Need help?
                        </TextButton>
                      ) : (
                        <TextButton
                          className={classNames(
                            classes.secondaryButton,
                            classes.resendOTPBtn,
                          )}
                          disabled={!variantMap.resendEnabled}
                          onClick={() =>
                            dispatch(loginActions.getOTPEmail(email))
                          }
                        >
                          Resend OTP
                        </TextButton>
                      )}
                    </LoginForm>
                    {isEmailView && ssoEnabled ? (
                      <>
                        <Typography className={classes.divider} variant="body2">
                          OR
                        </Typography>
                        <Button
                          className={classes.ssoButton}
                          variant="outlined"
                          color="primary"
                          size="large"
                          fullWidth
                          href={`/api/sso/login${
                            location?.state?.previous
                              ? `?next=${encodeURIComponent(
                                  location.state.previous,
                                )}`
                              : ''
                          }`}
                        >
                          Login with one.gov.sg
                        </Button>
                      </>
                    ) : null}
                    {variantMap.progressBarShown ? <LinearProgress /> : null}
                  </span>
                </section>
              </Section>
            </div>
          </div>
        </div>
      </BaseLayout>
    )
  }

  if (location) {
    // ensure page re-directed back to intended private route and reset the state
    if (location?.state?.previous) {
      return <Redirect to={{ pathname: location.state.previous, state: {} }} />
    }
    return <Redirect to={{ pathname: USER_PAGE, state: { from: location } }} />
  }
  return <Redirect to={{ pathname: USER_PAGE }} />
}

export default LoginPage
